import { MASK64, toHex64, fromHex64 } from '../model/hash';

/**
 * PCG32 (XSH RR 64/32), implemented with BigInt state and explicit 64-bit masking.
 *
 * Chosen over engine-provided randomness because the output sequence is defined entirely
 * by this file: the same seed produces the same stream in every browser and in Node.
 * All draws are integer draws — no floating point is involved in any logical decision.
 */

const MULTIPLIER = 6364136223846793005n;
const DEFAULT_STREAM = 1442695040888963407n;

export interface PrngState {
  readonly state: string;
  readonly inc: string;
}

export class Pcg32 {
  private state: bigint;
  private inc: bigint;

  constructor(seed: bigint, stream: bigint = DEFAULT_STREAM) {
    // The increment must be odd; the low bit is reserved by the algorithm.
    this.inc = ((stream << 1n) | 1n) & MASK64;
    this.state = 0n;
    this.step();
    this.state = (this.state + (seed & MASK64)) & MASK64;
    this.step();
  }

  /** Restores an exact stream position, so a paused generation run can resume identically. */
  static fromState(serialized: PrngState): Pcg32 {
    const restored = new Pcg32(0n);
    restored.inc = fromHex64(serialized.inc);
    restored.state = fromHex64(serialized.state);
    return restored;
  }

  getState(): PrngState {
    return { state: toHex64(this.state), inc: toHex64(this.inc) };
  }

  private step(): void {
    this.state = (this.state * MULTIPLIER + this.inc) & MASK64;
  }

  /** Uniform 32-bit value in [0, 2^32). */
  nextUint32(): number {
    const previous = this.state;
    this.step();
    const xorshifted = Number((((previous >> 18n) ^ previous) >> 27n) & 0xffffffffn) >>> 0;
    const rot = Number((previous >> 59n) & 31n);
    return ((xorshifted >>> rot) | (xorshifted << ((-rot >>> 0) & 31))) >>> 0;
  }

  /**
   * Uniform integer in [0, bound). Uses rejection sampling so the distribution is exactly
   * uniform and, crucially, deterministic — modulo bias would still be deterministic but
   * would skew generation heuristics in ways that are hard to reason about.
   */
  nextBelow(bound: number): number {
    if (!Number.isInteger(bound) || bound <= 0) {
      throw new RangeError(`bound must be a positive integer, received ${bound}`);
    }
    if (bound === 1) return 0;
    const threshold = (0x100000000 % bound) >>> 0;
    for (;;) {
      const value = this.nextUint32();
      if (value >= threshold) return value % bound;
    }
  }

  /** Uniform integer in [min, max], inclusive. */
  nextInt(min: number, max: number): number {
    if (max < min) throw new RangeError(`Empty range ${min}..${max}`);
    return min + this.nextBelow(max - min + 1);
  }

  nextBool(): boolean {
    return (this.nextUint32() & 1) === 1;
  }

  /** True with probability numerator/denominator, using integer arithmetic only. */
  chance(numerator: number, denominator: number): boolean {
    if (denominator <= 0) throw new RangeError('denominator must be positive');
    if (numerator <= 0) return false;
    if (numerator >= denominator) return true;
    return this.nextBelow(denominator) < numerator;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError('Cannot pick from an empty array');
    return items[this.nextBelow(items.length)];
  }

  /**
   * Picks an index with probability proportional to its integer weight.
   * Zero-weight entries are never selected; if every weight is zero, index 0 is returned.
   */
  weightedIndex(weights: ArrayLike<number>, count: number = weights.length): number {
    let total = 0;
    for (let i = 0; i < count; i += 1) {
      const weight = weights[i];
      if (!Number.isInteger(weight) || weight < 0) {
        throw new RangeError('Weights must be non-negative integers');
      }
      total += weight;
    }
    if (total <= 0) return 0;
    let roll = this.nextBelow(total);
    for (let i = 0; i < count; i += 1) {
      roll -= weights[i];
      if (roll < 0) return i;
    }
    return count - 1;
  }

  /** In-place Fisher-Yates shuffle. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i -= 1) {
      const j = this.nextBelow(i + 1);
      const swap = items[i];
      items[i] = items[j];
      items[j] = swap;
    }
    return items;
  }
}
